import { useCallback } from "react";
import { QueryClient } from "@tanstack/react-query";
import {
  GET_MEETING_PREP,
  GET_MEETING_PREP_ALL,
  GET_MEETING_PREP_EMAIL,
} from "@/api";
import { CloudEventType, CloudEvent } from "@/constants/websocket-events";
import { useWebSocketSubscription } from "@/context/WebSocketProvider";

interface Params {
  meetingPrepId: string;
  enabled: boolean;
  queryClient: QueryClient;
}

/**
 * Meeting prep is the realtime equivalent of the conversation subscription:
 * agent (and other server-side) writes to a prep doc or prep email emit
 * CloudEvents, and we invalidate the matching query so the panel refreshes
 * without a manual reload. Both prep and prep-email events are keyed by
 * meetingPrepId and routed through the single "meeting-prep" channel.
 */
export const useMeetingPrepWebSocketSubscriptions = ({
  meetingPrepId,
  enabled,
  queryClient,
}: Params) => {
  const handleWebSocketMessage = useCallback(
    (event: CloudEvent) => {
      const eventMeetingPrepId = (event.data as { meetingPrepId?: string })
        ?.meetingPrepId;

      if (eventMeetingPrepId !== meetingPrepId) {
        return;
      }

      switch (event.type) {
        case CloudEventType.MEETING_PREP_CREATED:
        case CloudEventType.MEETING_PREP_UPDATED:
          queryClient.invalidateQueries({
            queryKey: [GET_MEETING_PREP, meetingPrepId],
          });
          break;
        case CloudEventType.MEETING_PREP_DELETED:
          queryClient.invalidateQueries({
            queryKey: [GET_MEETING_PREP, meetingPrepId],
          });
          queryClient.invalidateQueries({ queryKey: [GET_MEETING_PREP_ALL] });
          break;
        case CloudEventType.MEETING_PREP_EMAIL_REGENERATED:
        case CloudEventType.MEETING_PREP_EMAIL_UPDATED:
          queryClient.invalidateQueries({
            queryKey: [GET_MEETING_PREP_EMAIL, meetingPrepId],
          });
          break;
      }
    },
    [meetingPrepId, queryClient],
  );

  const { isConnected: isWebSocketConnected } = useWebSocketSubscription(
    "meeting-prep",
    meetingPrepId,
    handleWebSocketMessage,
    enabled,
  );

  return { isWebSocketConnected };
};
